import {
  format,
  getMonth,
  isFirstDayOfMonth,
  isLastDayOfMonth,
  lastDayOfMonth,
  parse,
  startOfMonth,
} from "date-fns";
import { useEffect, useState } from "react";
import { EVENT_DATE_FORMAT } from "shared/utils";
import { DateRange } from "widgets/events/types";
import { useEventsWidgetContext } from "../EventsContext";

export type UseDateRangeCalendarPopOverProps = {
  dateRange: DateRange;
  startDate: string;
  onSelectedDatesChange: (dateRange: DateRange) => void;
  onClose: () => void;
};

export const useDateRangeCalendarPopOver = ({
  dateRange,
  startDate,
  onSelectedDatesChange,
  onClose,
}: UseDateRangeCalendarPopOverProps) => {
  const { locale } = useEventsWidgetContext();

  const [isOpen, setIsOpen] = useState(false);

  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [focusedValue, setFocusedValue] = useState<string | undefined>(
    undefined,
  );

  // Start the calendar with the currently selected month
  useEffect(() => {
    setFocusedValue(startDate);
    setTimeout(() => setFocusedValue(undefined), 100);
  }, [startDate]);

  /**
   * When the user clicks the "Save" button, we need to save the selected date range
   * - This may require a data load to get the events for the new date range
   */
  const handleSave = () => {
    const start = parse(selectedDates[0], EVENT_DATE_FORMAT, new Date());
    const end = parse(selectedDates[1], EVENT_DATE_FORMAT, new Date());
    onSelectedDatesChange?.({
      from: selectedDates[0],
      to: selectedDates[1],
      isFullMonth:
        isFirstDayOfMonth(start) &&
        isLastDayOfMonth(end) &&
        getMonth(start) === getMonth(end),
    });
    onClose?.();
  };

  /**
   * When we're in full-month mode & the calendar isn't open, clicking the
   * button will open the calendar
   * When the calendar is already open, clicking the button again will close it
   * When we're in selected-dates mode, clicking the button will reset the
   * dateRange to the current full-month
   */
  const handleClick = () => {
    if (dateRange.isFullMonth && !isOpen) {
      setIsOpen(true);
    } else if (isOpen) {
      setIsOpen(false);
    } else {
      const returnTo = parse(dateRange.to, EVENT_DATE_FORMAT, new Date());
      setSelectedDates([]);
      onSelectedDatesChange?.({
        from: format(startOfMonth(returnTo), EVENT_DATE_FORMAT, { locale }),
        to: format(lastDayOfMonth(returnTo), EVENT_DATE_FORMAT, { locale }),
        isFullMonth: true,
      });
    }
  };

  return {
    focusedValue,
    isOpen,
    selectedDates,
    handleClick,
    handleSave,
    setIsOpen,
    setSelectedDates,
  };
};
